import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface User {
  id: string;
  name?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface SearchableUserDropdownProps {
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  required?: boolean;
  label?: string;
  users?: User[]; // Optional: if provided, use this list instead of fetching all users
  filterUserIds?: string[]; // Optional: if provided, filter users to only show these IDs
  error?: string; // Optional: error message to display
}

const SearchableUserDropdown: React.FC<SearchableUserDropdownProps> = ({
  value,
  onChange,
  placeholder = 'Search users...',
  disabled = false,
  className = '',
  required = false,
  label,
  users: providedUsers,
  filterUserIds,
  error
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch users for dropdown (only if not provided via props)
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', 'dropdown', searchTerm],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      const response = await api.get(`/users/dropdown?${params}`);
      return response.data;
    },
    enabled: !providedUsers && (isOpen || !!value)
  });

  const users: User[] = providedUsers || usersData?.users || [];

  // Apply filterUserIds if provided
  const baseUsers = filterUserIds && filterUserIds.length > 0
    ? users.filter(user => filterUserIds.includes(user.id))
    : users;

  // Find selected user (search in all users, not just filtered ones)
  const selectedUser = users.find(user => user.id === value);

  useEffect(() => {
    if (selectedUser) {
      const fullName = selectedUser.name ||
        `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() ||
        selectedUser.email;
      setSelectedLabel(fullName);
    } else {
      setSelectedLabel('');
    }
  }, [selectedUser]);

  // Filter users based on search
  const filteredUsers = baseUsers.filter(user => {
    if (!searchTerm) return true;
    const fullName = user.name ||
      `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
      user.email;
    return fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           user.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (userId: string) => {
    onChange(userId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <button
        ref={buttonRef}
        type="button"
        className={`relative w-full bg-white border rounded-md pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 ${
          error
            ? 'border-red-300 focus:ring-red-400 focus:border-red-400'
            : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <span className="block truncate">
          {selectedLabel || placeholder}
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          {value && !disabled && (
            <span
              className="h-5 w-5 text-gray-400 hover:text-gray-500 mr-1 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <XMarkIcon className="h-5 w-5" />
            </span>
          )}
          <ChevronDownIcon
            className={`h-5 w-5 text-gray-400 transition-transform ${
              isOpen ? 'transform rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {/* Search input */}
          <div className="px-3 py-2 border-b border-gray-200">
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Loading users...
            </div>
          )}

          {/* Users list */}
          {!isLoading && filteredUsers.length > 0 ? (
            filteredUsers.map((user) => {
              const fullName = user.name || 
                `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                user.email;
              
              return (
                <div
                  key={user.id}
                  className={`cursor-pointer select-none relative px-3 py-2 hover:bg-indigo-600 hover:text-white ${
                    user.id === value ? 'bg-indigo-600 text-white' : 'text-gray-900'
                  }`}
                  onClick={() => handleSelect(user.id)}
                >
                  <div className="block">
                    <div className="font-medium">{fullName}</div>
                    {user.email && fullName !== user.email && (
                      <div className="text-sm opacity-75">{user.email}</div>
                    )}
                    {user.role && (
                      <div className="text-xs opacity-75 capitalize">
                        {typeof user.role === 'string' ? user.role.replace('_', ' ') : ((user.role as any)?.name || '').replace('_', ' ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : !isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              No users found
            </div>
          ) : null}
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SearchableUserDropdown;